import React, {Component} from 'react'
import propTypes from 'prop-types'
import {filter, find, get, has, isEmpty, isNil, last} from 'lodash'
import Abstract from 'components/abstract'
import Icon from 'components/icon'
import Node from './components/node'
import Option from './components/option'
import Placeholder from 'components/placeholder'
import {convertListToTree} from 'helpers/dataStructure'
import './treeSearcher.scss'

const TreeListType = {
  FLAT: 1,
  TREE: 2,
}

class TreeSearcher extends Component {
  constructor(props) {
    super(props)
    this.state = this.getInitialState(props)
  }

  getClass = Abstract.getClassGenerator('treeSearcher')

  getInitialState = (props) => {
    const {
      listType = TreeListType.TREE,
      folders,
      rootId = -1,
      parentLabel,
      data,
    } = props

    const currentNode = (listType === TreeListType.FLAT) ? convertListToTree(folders, rootId, parentLabel) : data
    return {
      parentNodes: [],
      currentNode,
      backClick: true,
      folders,
    }
  }

  onFolderClickHandler = (id) => {
    const parentNodes = [...this.state.parentNodes, this.state.currentNode]
    const currentNode = find(this.state.currentNode.children, ['id', id])
    this.setState({
      ...this.state,
      parentNodes,
      currentNode,
      backClick: false,
    })
    if (typeof this.props.onFolderClick === 'function') {
      this.props.onFolderClick(id)
    }
  }

  onOptionClickHandler = (id, toggled) => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(id, toggled)
    }
  }

  onBackClickHandler = () => {
    const currentNode = last(this.state.parentNodes)
    const parentNodes = this.state.parentNodes.slice(0, this.state.parentNodes.length - 1)
    this.setState({
      ...this.state,
      parentNodes,
      currentNode,
      backClick: true,
    })
    if (this.props.onBackClick != null) {
      this.props.onBackClick(currentNode.id)
    }
  }

  renderBody = (node, props, postfix = '', shallow = false) => {
    if (isNil(node)) return
    const key = this.getClass(`nodeBlock-${node.id}`)
    if (shallow) {
      return (<div className={this.getClass(`nodeBlock${postfix}`)} key={key}/>)
    }
    const childNodes = (has(node, 'children') === false)
      ? ''
      : node.children.map(n => {
        const icon = (has(n, 'icon') && isNil(n.icon) === false)
          ? n.icon
          : (has(props, 'folderIcon') && isNil(props.folderIcon) === false)
            ? props.folderIcon
            : 'folder_filled'
        return (
          <Node
            label={n.label}
            classFtn={this.getClass}
            onClickHandler={this.onFolderClickHandler}
            id={n.id}
            key={n.id}
            icon={icon}
          />
        )
      })

    const nodeOptions = has(props, 'options')
      ? filter(props.options, ['parentId', node.id])
      : get(node, 'options')

    const options = nodeOptions
      ? nodeOptions.map(n => {
        const icon = (has(n, 'icon') && isNil(n.icon) === false)
          ? n.icon
          : (has(props, 'optionIcon') && isNil(props.optionIcon) === false)
            ? props.optionIcon
            : 'right'
        return (
          <Option
            label={n.label}
            showCheck={props.multiSelect}
            checked={props.multiSelect && props.value && props.value.some(v => v === n.id)}
            classFtn={this.getClass}
            onClickHandler={this.onOptionClickHandler}
            id={n.id}
            key={n.id}
            icon={icon}
          />
        )
      })
      : null

    return (
      <div className={this.getClass(`nodeBlock${postfix}`)} key={key}>
        <ul className={this.getClass('childNodes')} id={this.getClass(`childNodes-${node.id}`)}>
          {childNodes}
        </ul>
        <ul className={this.getClass('options')}>
          {options}
        </ul>
      </div>
    )
  }

  getPlaceHolder = () => {
    const {placeHolderData, folders, options} = this.props
    return !isNil(placeHolderData) && isEmpty(folders) && isEmpty(options)
      ? <Placeholder {...placeHolderData}/>
      : null
  }

  render() {
    const {currentNode, parentNodes, backClick} = this.state

    const back = (this.state.parentNodes.length > 0)
      ? (
        <div className={this.getClass('back')} onClick={this.onBackClickHandler}>
          <Icon name="back"/>
        </div>
      )
      : (
        <div className={this.getClass('back disabled')}>
          &nbsp;
        </div>
      )

    const headerAction = has(this, 'props.headerAction') ? this.props.headerAction : null
    const parentPostfix = backClick ? ' parent hidden' : ' parent'
    const activePostfix = backClick ? '' : ' active'
    const childBodies = has(currentNode, 'children')
      ? currentNode.children.map(c => this.renderBody(c, this.props, ' next', true))
      : null

    const parentNodesBody = this.renderBody(last(parentNodes), this.props, parentPostfix)
    const currentNodeBody = this.renderBody(currentNode, this.props, activePostfix)

    return (
      <div className={this.getClass('container')}>
        <div className={this.getClass('header')}>
          {back}
          <div className={this.getClass('title')}>{currentNode.label}</div>
          {headerAction}
        </div>
        <div className={this.getClass('body')}>
          {parentNodesBody}
          {currentNodeBody}
          {childBodies}
          {this.getPlaceHolder()}
        </div>
      </div>
    )
  }
}

TreeSearcher.propTypes = {
  listType: propTypes.oneOf([1, 2]),
  data: propTypes.oneOfType([
    propTypes.array,
    propTypes.object,
  ]),
  rootId: propTypes.number,
  options: propTypes.array,
  parentLabel: propTypes.string,
  folders: propTypes.array,
  folderIcon: propTypes.oneOfType([
    propTypes.instanceOf(Icon),
    propTypes.string,
  ]),
  optionIcon: propTypes.oneOfType([
    propTypes.instanceOf(Icon),
    propTypes.string,
  ]),
  multiSelect: propTypes.bool,
  value: propTypes.arrayOf(propTypes.number), // specify if multiSelect === true
  onClick: propTypes.func,
  onFolderClick: propTypes.func,
  onChange: propTypes.func,
  onBackClick: propTypes.func,
  headerAction: propTypes.any,
  placeHolderData: propTypes.object,
}

export {
  TreeListType,
  Node,
  Option,
  convertListToTree,
}
export default TreeSearcher
